import React, { useState, useEffect, useCallback } from "react";
import SimulationDataGeneratorAPI from "../services/SimulationDataGeneratorAPI";
import LoadingSpinner from "../common/LoadingSpinner";
import DateRangeField from "../../common/datetime/DateRangeField";

/**
 * Message Library tab — browse, search, edit, and delete production messages.
 * Reuses the same editor pattern as MessageEditor but for production messages.
 */
const MessageLibrary = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [contextFilter, setContextFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [allContexts, setAllContexts] = useState([]);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (searchQuery.trim()) {
        data = await SimulationDataGeneratorAPI.searchProductionMessages(
          searchQuery.trim(),
        );
      } else {
        data = await SimulationDataGeneratorAPI.getProductionMessages();
      }
      const msgArray = Array.isArray(data) ? data : [];
      setMessages(msgArray);
      // Populate the stable context list from the full unfiltered set
      if (!searchQuery.trim()) {
        const contexts = [
          ...new Set(
            msgArray
              .map((m) => m.communicationContext)
              .filter(Boolean)
              .flatMap((c) => c.split(",").map((s) => s.trim()))
              .filter(Boolean),
          ),
        ].sort();
        setAllContexts(contexts);
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setError("Failed to load messages. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(
      () => {
        fetchMessages();
      },
      searchQuery ? 400 : 0,
    );
    return () => clearTimeout(debounce);
  }, [fetchMessages, searchQuery]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleEdit = (message) => {
    setEditingMessage({
      ...message,
      contentPlainText: message.contentPlainText || message.content || "",
      contentHtml: message.contentHtml || "",
    });
  };

  const handleSave = async () => {
    if (!editingMessage) return;
    setSaving(true);
    try {
      await SimulationDataGeneratorAPI.updateProductionMessage(
        editingMessage.uuid,
        {
          subject: editingMessage.subject,
          contentPlainText: editingMessage.contentPlainText,
          contentHtml:
            editingMessage.communicationChannel?.toUpperCase() === "EMAIL"
              ? editingMessage.contentHtml
              : null,
          communicationContext: editingMessage.communicationContext,
          communicationChannel: editingMessage.communicationChannel,
          sophisticationLevel: parseInt(editingMessage.sophisticationLevel),
          isActive: editingMessage.isActive !== false,
          activePeriodType: editingMessage.activePeriodType || "ALWAYS",
          activeStartMonth:
            editingMessage.activePeriodType === "SEASONAL"
              ? parseInt(editingMessage.activeStartMonth) || null
              : null,
          activeEndMonth:
            editingMessage.activePeriodType === "SEASONAL"
              ? parseInt(editingMessage.activeEndMonth) || null
              : null,
          activeStartDate:
            editingMessage.activePeriodType === "EVENT"
              ? editingMessage.activeStartDate || null
              : null,
          activeEndDate:
            editingMessage.activePeriodType === "EVENT"
              ? editingMessage.activeEndDate || null
              : null,
        },
      );
      showSuccess("Message updated successfully.");
      setEditingMessage(null);
      fetchMessages();
    } catch (err) {
      console.error("Failed to update message:", err);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (uuid) => {
    try {
      await SimulationDataGeneratorAPI.deleteProductionMessage(uuid);
      showSuccess("Message deleted.");
      setDeleteConfirmId(null);
      if (expandedId === uuid) setExpandedId(null);
      fetchMessages();
    } catch (err) {
      console.error("Failed to delete message:", err);
      setError("Failed to delete message. Please try again.");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setChannelFilter("");
    setContextFilter("");
    setLevelFilter("");
    setStatusFilter("");
    setPeriodFilter("");
  };

  const getSophisticationLabel = (level) => {
    if (level <= 3) return "Low";
    if (level <= 6) return "Medium";
    if (level <= 9) return "High";
    return "Expert";
  };

  const getSophisticationClass = (level) => {
    if (level <= 3) return "soph-low";
    if (level <= 6) return "soph-medium";
    if (level <= 9) return "soph-high";
    return "soph-expert";
  };

  const getCharCountClass = (current, max) => {
    const ratio = current / max;
    if (ratio >= 1) return "danger";
    if (ratio >= 0.9) return "warning";
    return "";
  };

  /**
   * When searching, extract a short snippet around the first match in content
   * and highlight the matched term. Returns JSX or null.
   */
  const getContentSnippet = (msg) => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.trim().toLowerCase();
    const plain = (msg.contentPlainText || msg.content || "").replace(
      /<[^>]*>/g,
      "",
    );
    const idx = plain.toLowerCase().indexOf(q);
    if (idx === -1) return null; // match was in subject or HTML only

    const snippetRadius = 60;
    const start = Math.max(0, idx - snippetRadius);
    const end = Math.min(plain.length, idx + q.length + snippetRadius);
    const before = (start > 0 ? "..." : "") + plain.slice(start, idx);
    const match = plain.slice(idx, idx + q.length);
    const after =
      plain.slice(idx + q.length, end) + (end < plain.length ? "..." : "");

    return (
      <span className="library-search-snippet">
        {before}
        <mark>{match}</mark>
        {after}
      </span>
    );
  };

  // Use the stable context list for the dropdown (populated from unfiltered data)
  const uniqueContexts = allContexts;

  // Apply all filters client-side (channel, context, level, status, period)
  const filteredMessages = messages.filter((m) => {
    // Channel filter — normalize both sides (DB may store "email" or "EMAIL")
    if (channelFilter) {
      const stored = (m.communicationChannel || "")
        .toUpperCase()
        .replace(/-/g, "_");
      if (stored !== channelFilter.toUpperCase().replace(/-/g, "_"))
        return false;
    }
    // Context filter — check if any comma-delimited token matches
    if (contextFilter) {
      const ctx = m.communicationContext || "";
      const tokens = ctx.split(",").map((t) => t.trim().toLowerCase());
      if (!tokens.includes(contextFilter.trim().toLowerCase())) return false;
    }
    // Level filter
    if (levelFilter) {
      const lvl = m.sophisticationLevel;
      if (levelFilter === "low" && !(lvl >= 1 && lvl <= 3)) return false;
      if (levelFilter === "medium" && !(lvl >= 4 && lvl <= 6)) return false;
      if (levelFilter === "high" && !(lvl >= 7 && lvl <= 9)) return false;
      if (levelFilter === "expert" && lvl !== 10) return false;
    }
    // Status filter (active / inactive)
    if (statusFilter) {
      const active = m.isActive !== false; // default true if null/undefined
      if (statusFilter === "active" && !active) return false;
      if (statusFilter === "inactive" && active) return false;
    }
    // Period type filter
    if (periodFilter) {
      const pt = (m.activePeriodType || "ALWAYS").toUpperCase();
      if (pt !== periodFilter.toUpperCase()) return false;
    }
    return true;
  });

  return (
    <div className="message-library">
      {/* Success banner */}
      {successMsg && <div className="library-success">{successMsg}</div>}

      {/* Error banner */}
      {error && (
        <div className="library-error">
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Filters toolbar */}
      <div className="library-filters">
        <div className="library-search">
          <input
            type="text"
            placeholder="Search subject & content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="library-filter-group">
          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
            disabled={!!searchQuery.trim()}
          >
            <option value="">All Channels</option>
            <option value="EMAIL">Email</option>
            <option value="SMS">SMS</option>
            <option value="VOICE">Voice</option>
            <option value="SOCIAL_MEDIA">Social Media</option>
          </select>

          <select
            value={contextFilter}
            onChange={(e) => setContextFilter(e.target.value)}
            disabled={!!searchQuery.trim()}
          >
            <option value="">All Contexts</option>
            {uniqueContexts.map((ctx) => (
              <option key={ctx} value={ctx}>
                {ctx}
              </option>
            ))}
          </select>

          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <option value="">All Levels</option>
            <option value="low">Low (1-3)</option>
            <option value="medium">Medium (4-6)</option>
            <option value="high">High (7-9)</option>
            <option value="expert">Expert (10)</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
          >
            <option value="">All Periods</option>
            <option value="ALWAYS">Always</option>
            <option value="SEASONAL">Seasonal</option>
            <option value="EVENT">Event</option>
          </select>

          {(searchQuery ||
            channelFilter ||
            contextFilter ||
            levelFilter ||
            statusFilter ||
            periodFilter) && (
            <button className="library-clear-btn" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>

        <div className="library-count">
          {filteredMessages.length} message
          {filteredMessages.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Message list */}
      {loading ? (
        <LoadingSpinner
          message="Loading messages..."
          size="sm"
          className="compact"
        />
      ) : filteredMessages.length === 0 ? (
        <div className="library-empty">
          No messages found.
          {(searchQuery ||
            channelFilter ||
            contextFilter ||
            levelFilter ||
            statusFilter ||
            periodFilter) && (
            <button onClick={clearFilters}>Clear filters</button>
          )}
        </div>
      ) : (
        <div className="library-list">
          <div className="library-list-header">
            <div className="library-card-info">
              <span className="library-header-id">ID</span>
              <span className="library-header-channel">Channel</span>
              <span className="library-header-subject">Subject</span>
            </div>
            <div className="library-card-meta">
              <span className="library-header-date">Created</span>
              <span className="library-header-date">Updated</span>
              <span className="library-header-soph">Level</span>
              <span
                className="library-expand-icon"
                style={{ visibility: "hidden" }}
              >
                ▼
              </span>
            </div>
          </div>
          {filteredMessages.map((msg) => (
            <div
              key={msg.messageId}
              className={`library-card ${expandedId === msg.messageId ? "expanded" : ""}`}
            >
              <div
                className="library-card-header"
                onClick={() =>
                  setExpandedId(
                    expandedId === msg.messageId ? null : msg.messageId,
                  )
                }
              >
                <div className="library-card-info">
                  <span className="library-id-badge">#{msg.messageId}</span>
                  <span
                    className={`library-channel-badge ${msg.communicationChannel?.toLowerCase()}`}
                  >
                    {msg.communicationChannel}
                  </span>
                  <span
                    className={`library-status-badge ${msg.isActive !== false ? "active" : "inactive"}`}
                  >
                    {msg.isActive !== false ? "Active" : "Inactive"}
                  </span>
                  <span className="library-card-subject">
                    {msg.subject || "(no subject)"}
                  </span>
                  {searchQuery.trim() && getContentSnippet(msg) && (
                    <div className="library-content-match">
                      {getContentSnippet(msg)}
                    </div>
                  )}
                </div>
                <div className="library-card-meta">
                  <span className="library-date-col" title="Created">
                    {msg.createdAt
                      ? new Date(msg.createdAt).toLocaleDateString()
                      : "—"}
                  </span>
                  <span className="library-date-col" title="Updated">
                    {msg.updatedAt
                      ? new Date(msg.updatedAt).toLocaleDateString()
                      : "—"}
                  </span>
                  <span
                    className={`library-soph-badge ${getSophisticationClass(msg.sophisticationLevel)}`}
                  >
                    L{msg.sophisticationLevel} —{" "}
                    {getSophisticationLabel(msg.sophisticationLevel)}
                  </span>
                  <span className="library-expand-icon">
                    {expandedId === msg.messageId ? "▲" : "▼"}
                  </span>
                </div>
              </div>

              {expandedId === msg.messageId && (
                <div className="library-card-body">
                  <div className="library-card-detail">
                    <strong>Context:</strong> {msg.communicationContext || "—"}
                  </div>
                  <div className="library-card-detail">
                    <strong>Active Period:</strong>{" "}
                    {(() => {
                      const pt = (
                        msg.activePeriodType || "ALWAYS"
                      ).toUpperCase();
                      const monthNames = [
                        "",
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec",
                      ];
                      if (
                        pt === "SEASONAL" &&
                        msg.activeStartMonth &&
                        msg.activeEndMonth
                      ) {
                        return `Seasonal — ${monthNames[msg.activeStartMonth]} to ${monthNames[msg.activeEndMonth]}`;
                      }
                      if (pt === "EVENT" && msg.activeStartDate) {
                        return `Event — ${msg.activeStartDate}${msg.activeEndDate ? " to " + msg.activeEndDate : " (ongoing)"}`;
                      }
                      return "Always";
                    })()}
                  </div>
                  <div className="library-card-detail">
                    <strong>Plain Text:</strong>
                    <div className="library-preview-text">
                      {msg.contentPlainText || msg.content || "—"}
                    </div>
                  </div>
                  {msg.communicationChannel?.toUpperCase() === "EMAIL" &&
                    msg.contentHtml && (
                      <div className="library-card-detail">
                        <strong>HTML Preview:</strong>
                        <div
                          className="library-preview-html"
                          dangerouslySetInnerHTML={{ __html: msg.contentHtml }}
                        />
                      </div>
                    )}
                  <div className="library-card-detail library-card-dates">
                    <span>
                      Created:{" "}
                      {msg.createdAt
                        ? new Date(msg.createdAt).toLocaleString()
                        : "—"}
                    </span>
                    <span>
                      Updated:{" "}
                      {msg.updatedAt
                        ? new Date(msg.updatedAt).toLocaleString()
                        : "—"}
                    </span>
                  </div>
                  <div className="library-card-actions">
                    <button
                      className="library-edit-btn"
                      onClick={() => handleEdit(msg)}
                    >
                      Edit
                    </button>
                    {deleteConfirmId === msg.uuid ? (
                      <span className="library-delete-confirm">
                        Are you sure?
                        <button
                          className="library-delete-yes"
                          onClick={() => handleDelete(msg.uuid)}
                        >
                          Yes, Delete
                        </button>
                        <button
                          className="library-delete-no"
                          onClick={() => setDeleteConfirmId(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className="library-delete-btn"
                        onClick={() => setDeleteConfirmId(msg.uuid)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editingMessage && (
        <div
          className="message-editor-overlay"
          onClick={() => setEditingMessage(null)}
        >
          <div className="message-editor" onClick={(e) => e.stopPropagation()}>
            <div className="message-editor-header">
              <h3>Edit Message</h3>
              <button
                className="editor-close-btn"
                onClick={() => setEditingMessage(null)}
              >
                &times;
              </button>
            </div>

            <div className="message-editor-body">
              {/* Subject */}
              <div className="editor-field">
                <label>Subject</label>
                <input
                  type="text"
                  value={editingMessage.subject || ""}
                  onChange={(e) =>
                    setEditingMessage({
                      ...editingMessage,
                      subject: e.target.value,
                    })
                  }
                  maxLength={255}
                />
                <span
                  className={`char-count ${getCharCountClass((editingMessage.subject || "").length, 255)}`}
                >
                  {(editingMessage.subject || "").length}/255
                </span>
              </div>

              {/* Plain Text Content */}
              <div className="editor-field">
                <label>Plain Text Content</label>
                <textarea
                  value={editingMessage.contentPlainText || ""}
                  onChange={(e) =>
                    setEditingMessage({
                      ...editingMessage,
                      contentPlainText: e.target.value,
                    })
                  }
                  maxLength={512}
                  rows={4}
                />
                <span
                  className={`char-count ${getCharCountClass((editingMessage.contentPlainText || "").length, 512)}`}
                >
                  {(editingMessage.contentPlainText || "").length}/512
                </span>
              </div>

              {/* HTML Content (email only) */}
              {editingMessage.communicationChannel?.toUpperCase() ===
                "EMAIL" && (
                <div className="editor-field">
                  <label>HTML Content</label>
                  <textarea
                    value={editingMessage.contentHtml || ""}
                    onChange={(e) =>
                      setEditingMessage({
                        ...editingMessage,
                        contentHtml: e.target.value,
                      })
                    }
                    maxLength={5000}
                    rows={6}
                    style={{ fontFamily: "monospace", fontSize: "0.82rem" }}
                  />
                  <span
                    className={`char-count ${getCharCountClass((editingMessage.contentHtml || "").length, 5000)}`}
                  >
                    {(editingMessage.contentHtml || "").length}/5000
                  </span>
                </div>
              )}

              {/* Context and Level */}
              <div style={{ display: "flex", gap: "16px" }}>
                <div className="editor-field" style={{ flex: 2 }}>
                  <label>Communication Context (comma-separated)</label>
                  <input
                    type="text"
                    value={editingMessage.communicationContext || ""}
                    onChange={(e) =>
                      setEditingMessage({
                        ...editingMessage,
                        communicationContext: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="editor-field" style={{ flex: 1 }}>
                  <label>Sophistication Level (1-10)</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={editingMessage.sophisticationLevel || 5}
                    onChange={(e) =>
                      setEditingMessage({
                        ...editingMessage,
                        sophisticationLevel: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              {/* Active Period Settings */}
              <div className="editor-field-group">
                <div className="active-period-header">
                  <div className="editor-field" style={{ flex: 0 }}>
                    <label>Active</label>
                    <label className="editor-toggle">
                      <input
                        type="checkbox"
                        checked={editingMessage.isActive !== false}
                        onChange={(e) =>
                          setEditingMessage({
                            ...editingMessage,
                            isActive: e.target.checked,
                          })
                        }
                      />
                      <span>
                        {editingMessage.isActive !== false ? "Yes" : "No"}
                      </span>
                    </label>
                  </div>
                  <div className="editor-field" style={{ flex: 1 }}>
                    <label>Period Type</label>
                    <select
                      value={editingMessage.activePeriodType || "ALWAYS"}
                      onChange={(e) =>
                        setEditingMessage({
                          ...editingMessage,
                          activePeriodType: e.target.value,
                          ...(e.target.value !== "SEASONAL"
                            ? { activeStartMonth: null, activeEndMonth: null }
                            : {}),
                          ...(e.target.value !== "EVENT"
                            ? { activeStartDate: null, activeEndDate: null }
                            : {}),
                        })
                      }
                    >
                      <option value="ALWAYS">Always</option>
                      <option value="SEASONAL">Seasonal (months)</option>
                      <option value="EVENT">Event (specific dates)</option>
                    </select>
                  </div>
                </div>

                {/* Seasonal month range */}
                {editingMessage.activePeriodType === "SEASONAL" && (
                  <div className="active-period-row">
                    <div className="editor-field" style={{ flex: 1 }}>
                      <label>Start Month</label>
                      <select
                        value={editingMessage.activeStartMonth || ""}
                        onChange={(e) =>
                          setEditingMessage({
                            ...editingMessage,
                            activeStartMonth: parseInt(e.target.value) || null,
                          })
                        }
                      >
                        <option value="">Select...</option>
                        {[
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ].map((m, i) => (
                          <option key={i + 1} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="editor-field" style={{ flex: 1 }}>
                      <label>End Month</label>
                      <select
                        value={editingMessage.activeEndMonth || ""}
                        onChange={(e) =>
                          setEditingMessage({
                            ...editingMessage,
                            activeEndMonth: parseInt(e.target.value) || null,
                          })
                        }
                      >
                        <option value="">Select...</option>
                        {[
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ].map((m, i) => (
                          <option key={i + 1} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Event date range */}
                {editingMessage.activePeriodType === "EVENT" && (
                  <div className="active-period-row">
                    <DateRangeField
                      legend="Active period"
                      fromLabel="Start date"
                      toLabel="End date"
                      futureAllowed
                      presets={[]}
                      from={editingMessage.activeStartDate || ""}
                      to={editingMessage.activeEndDate || ""}
                      onChange={({ from, to }) =>
                        setEditingMessage({
                          ...editingMessage,
                          activeStartDate: from || null,
                          activeEndDate: to || null,
                        })
                      }
                    />
                  </div>
                )}
              </div>

              {/* HTML Preview */}
              {editingMessage.communicationChannel?.toUpperCase() === "EMAIL" &&
                editingMessage.contentHtml && (
                  <div className="editor-preview">
                    <div className="preview-section">
                      <h4>HTML Preview</h4>
                      <div
                        className="html-preview"
                        dangerouslySetInnerHTML={{
                          __html: editingMessage.contentHtml,
                        }}
                      />
                    </div>
                    <div className="preview-section">
                      <h4>Plain Text Preview</h4>
                      <div className="plain-text-preview">
                        {editingMessage.contentPlainText}
                      </div>
                    </div>
                  </div>
                )}
            </div>

            <div className="message-editor-footer">
              <button
                className="editor-cancel-btn"
                onClick={() => setEditingMessage(null)}
              >
                Cancel
              </button>
              <button
                className="editor-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageLibrary;
